import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover, {HoverPopoverProps} from 'ui-library/floating-ui/Popover';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface BaseProps {
  content: ReactNode;
  className?: string;
  dataTestAttrs?: DataTestAttrs;
}

function BaseWarningBadge({content, className, dataTestAttrs}: BaseProps): JSX.Element {
  return (
    <span className={classNames(css(styles.warningBadge), className)} {...getTestProps(dataTestAttrs)}>
      <span
        className={classNames(
          css(styles.warningBadgeIcon),
          content != null && content !== '' && 'u-tiny-padding-right'
        )}
      >
        <BenchlingIcon icon="warning" />
      </span>
      {content}
    </span>
  );
}

/**
 * A warning badge wrapped in a tooltip.
 */
export default function TooltipWarningBadge({
  content,
  className,
  tooltipTitle,
  dataTestAttrs,
}: BaseProps & {tooltipTitle: string}): JSX.Element {
  return (
    <Tooltip placement={FloatingPlacement.TOP} text={tooltipTitle}>
      <BaseWarningBadge content={content} className={className} dataTestAttrs={dataTestAttrs} />
    </Tooltip>
  );
}

/**
 * A warning badge wrapped in a hover Popover.
 */
export function PopoverWarningBadge({
  content,
  className,
  popoverProps,
  dataTestAttrs,
}: BaseProps & {popoverProps: Omit<HoverPopoverProps, 'children'>}): JSX.Element {
  return (
    <Popover {...popoverProps}>
      <BaseWarningBadge content={content} className={className} dataTestAttrs={dataTestAttrs} />
    </Popover>
  );
}

const styles = StyleSheet.create({
  warningBadge: {
    borderRadius: 2,
    background: Colors.ORANGE_LIGHTER,
    color: Colors.ORANGE,
    padding: '3px 7px',
  },
  warningBadgeIcon: {
    color: Colors.ORANGE,
  },
});
